import { toast } from '@wellesley/system-toast'
import { useParams } from 'react-router-dom'
import { CreatePost } from '@wellesley/openapi'
import { useDispatch } from 'react-redux'
import { getErrorMsg } from '@wellesley/error-kit'
import { getCommentRoute } from 'helpers/routes'
import { useAppsNavigate } from '@wellesley/router'
import { useRef, useEffect } from 'react'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { feedsSlice, Post } from '../../../feed/feedsSlice'
import { usePostApiV1PostsByPostIdCommentsMutation } from '../../../feed/api'

type Props = {
  postId: string
}

const useMakeComment = ({ postId }: Props) => {
  const dispatch = useDispatch()
  const navigate = useAppsNavigate()
  const [makeComment] = usePostApiV1PostsByPostIdCommentsMutation()
  const { postId: postIdParam } = useParams<'postId'>()

  const canScrollToReply = !!postIdParam

  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }
  useEffect(
    () => () => {
      clearTimer()
    },
    [],
  )

  const navigateToReply = (commentData: Post) =>
    navigate(getCommentRoute(commentData.author, commentData.id))

  const handleMakeComment = async (data: CreatePost) => {
    const toastId = 'make-comment'
    toast.loading('Posting...', { id: toastId })

    try {
      const commentData = await makeComment({
        postId,
        createPost: data,
      }).unwrap()

      toast.dismiss(toastId)
      dispatch(feedsSlice.actions.addComment(commentData))

      const onViewReply = () => {
        clearTimer()
        if (canScrollToReply) {
          const replyParentId = commentData.path.at(-1) ?? ''
          if (replyParentId) {
            dispatch(
              feedsSlice.actions.toggleNestedComments({
                id: replyParentId,
              }),
            )
            timerRef.current = setTimeout(() => {
              const replyElement = document.getElementById(
                `reply-feed-${commentData.id}`,
              )
              replyElement?.scrollIntoView({ behavior: 'smooth' })
            }, 0)
          } else {
            navigateToReply(commentData)
          }
        } else {
          navigateToReply(commentData)
        }
      }

      toast.success('Reply published', {
        actionButton: {
          label: 'View',
          onClick: onViewReply,
        },
      })
    } catch (error) {
      toast.dismiss(toastId)
      toast.error(
        `Failed to create comment ${getErrorMsg(error as FetchBaseQueryError)}`,
      )
    }
  }

  return handleMakeComment
}

export default useMakeComment
